import { queryOptions, useQueries, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { createApiClient } from '../../api/helpers.ts'
import { type Dayjs, dayjs } from '../../dayjs.ts'
import { env } from '../../env.ts'
import { useFlags } from '../../launchdarkly.ts'
import { encodeImpersonation, type Impersonation, useImpersonation } from '../impersonation.ts'
import { LTZ_SUFFIX } from '../protections/lightning.ts'
import { ltzProtectionAssetsQuery } from '../protections/protections.ts'
import { IncomingLightningPriority, ProtectionPriority, ProtectionType } from '../protections/types.ts'
import { createTemporaryLightningProtectionVrn, lightningPriorityToSeverityMap } from '../protections/utils.ts'
import { useSubscription } from '../service.ts'
import { alertsQuery } from './alerts.ts'
import type { AnyAlert, LightningAlert } from './types.ts'

const PAGE_SIZE = 160
const MAX_PAGES = 200

const LightningApi = createApiClient({ prefixUrl: env.API_LIGHTNING })

export const realtimeLightningAlertQuery = (
	impersonation: Impersonation | undefined,
	options: { includeAssetName?: boolean; enabled?: boolean } = {},
) =>
	queryOptions({
		queryKey: ['LIGHTNING_ALERTS', impersonation?.subscriptionId, options.includeAssetName],
		refetchInterval: 1000 * 60,
		staleTime: 1000 * 60,
		enabled: options.enabled,
		refetchIntervalInBackground: true, // To make audible alerts work while tab is in the background
		queryFn: async (): Promise<LightningAlert[]> => {
			const { includeAssetName = false } = options
			const retrieveAlertPages = async (page: number = 0, alerts: IncomingLightningAlert[] = []) => {
				const response = await LightningApi.get('api/v2/alert/events', {
					searchParams: [
						['page', page],
						['size', PAGE_SIZE],
						['impersonation', encodeImpersonation(impersonation) ?? ''],
						['assetName', includeAssetName],
					],
				})
					.then((res) => res.json())
					.catch((error) => {
						console.error('Error fetching lightning alerts:', error)
						return []
					})

				const arr = [...alerts, ...(response as IncomingLightningAlert[])]

				if ((response as IncomingLightningAlert[]).length >= PAGE_SIZE) return retrieveAlertPages(page + 1, arr)

				return arr
			}
			const response = await retrieveAlertPages()
			return response.map<LightningAlert>((alert) => ({
				protectionVrn: createTemporaryLightningProtectionVrn(alert.data.assetVrn, alert.data.priority),
				type: ProtectionType.Lightning,
				context: {},
				eventTime: alert.data.estClearTime,
				startTime: alert.data.lastTrigger,
				firstTrigger: alert.data.firstTrigger,
				currentState: alert.data.currentState,
				extent: alert.data.extent,
				firstEvent: alert.data.firstEvent,
				lastEvent: alert.data.lastEvent,
				priority: alert.data.priority,
				severity: lightningPriorityToSeverityMap[alert.data.priority],
				assetName: includeAssetName ? alert.data.assetName : undefined,
			}))
		},
	})

export const useLightningAlerts = () =>
	useQuery(realtimeLightningAlertQuery(useImpersonation(), { enabled: useSubscription().data.features.includes('protections') }))

export const forecastLightningQuery = (impersonation: Impersonation | undefined, options: { enabled?: boolean } = {}) =>
	queryOptions({
		queryKey: ['LIGHTNING_THREATZONE_ALERTS', impersonation?.subscriptionId],
		refetchInterval: 1000 * 60,
		staleTime: 1000 * 60 * 60,
		enabled: options.enabled,
		queryFn: async (): Promise<LightningAlert[]> => {
			const response = await LightningApi.get('api/v2/alert/ltz/event', {
				searchParams: [
					['time', dayjs().toISOString()],
					['impersonation', encodeImpersonation(impersonation) ?? ''],
				],
			}).json<IncomingLightningThreatzoneAlert[]>()
			return response.map((alert) => ({
				protectionVrn: createTemporaryLightningProtectionVrn(`${alert.assetVrn}${LTZ_SUFFIX}`, ProtectionPriority.LightningAlarm),
				type: ProtectionType.Lightning,
				context: { LIGHTNING_THREAT_LEADTIME: alert.horizon },
				eventTime: alert.eventTime,
				startTime: alert.eventTime,
				firstTrigger: alert.eventTime,
				priority: 1,
				severity: 'warning',
			}))
		},
	})

export const toAssetLtzAlerts = (alerts: AnyAlert[], ltzProtectionAssets: Record<string, string>): LightningAlert[] =>
	alerts.flatMap((alert) => {
		if (alert.type !== ProtectionType.Lightning) return []
		const assetVrn = ltzProtectionAssets[alert.protectionVrn]
		if (!assetVrn) return []
		return [
			{ ...alert, protectionVrn: createTemporaryLightningProtectionVrn(`${assetVrn}${LTZ_SUFFIX}`, ProtectionPriority.LightningAlarm) },
		]
	})

export const useLtzAlerts = () => {
	const impersonation = useImpersonation()
	const enabled = useSubscription().data.features.includes('protections')
	const { ltzAlertsV2 } = useFlags()
	return useQueries({
		queries: [
			forecastLightningQuery(impersonation, { enabled: ltzAlertsV2 ? false : enabled }),
			alertsQuery(impersonation, { enabled: ltzAlertsV2 ? enabled : false }),
			ltzProtectionAssetsQuery(impersonation, { enabled: ltzAlertsV2 ? enabled : false }),
		],
		combine: ([legacy, alerts, ltzProtectionAssets]) =>
			ltzAlertsV2
				? {
						data: toAssetLtzAlerts(alerts.data ?? [], ltzProtectionAssets.data ?? {}),
						isPending: alerts.isPending || ltzProtectionAssets.isPending,
						isLoading: alerts.isLoading || ltzProtectionAssets.isLoading,
					}
				: { data: legacy.data ?? [], isPending: legacy.isPending, isLoading: legacy.isLoading },
	})
}

export const useCombinedLightningAlerts = () => {
	const { data: realtimeAlerts = [] } = useLightningAlerts()
	const { data: threatzoneAlerts } = useLtzAlerts()
	const { ltzAlertsV2 } = useFlags()

	const filteredAlerts = ltzAlertsV2
		? threatzoneAlerts
		: threatzoneAlerts.filter((ltzAlert) => {
				const assetVrn = ltzAlert.protectionVrn.replace(LTZ_SUFFIX, '')
				return !realtimeAlerts.some((rtAlert) => rtAlert.protectionVrn === assetVrn && rtAlert.priority === 1)
			})

	return {
		realtimeAlerts,
		threatzoneAlerts: filteredAlerts,
	}
}

export const historicalLightningAlertsQuery = (
	START_TIME: string,
	END_TIME: string,
	impersonation: Impersonation | undefined,
	options: { enabled?: boolean; includeAssetName?: boolean; keepOldDataWhileFetching?: boolean } = {},
) => {
	const { enabled = true, includeAssetName = false, keepOldDataWhileFetching = false } = options
	return queryOptions({
		queryKey: ['HISTORIC_LIGHTNING_ALERTS', START_TIME, END_TIME, impersonation?.subscriptionId, includeAssetName],
		placeholderData: keepOldDataWhileFetching ? (previousData) => previousData : undefined,
		queryFn: async () => {
			const retrieveAlertPages = async (page: number = 0, alerts: IncomingLightningAlert[] = []) => {
				if (page > MAX_PAGES) {
					console.warn(`Hit max recursion of ${MAX_PAGES} pages when fetching csv alerts`)
					return alerts
				}
				const searchParams = [
					['page', page],
					['size', PAGE_SIZE], // Often up to double the size
					['impersonation', encodeImpersonation(impersonation) ?? ''],
					['assetName', includeAssetName],
				]
				if (START_TIME) searchParams.push(['startTime', START_TIME])
				if (END_TIME) searchParams.push(['endTime', END_TIME])
				if (START_TIME && END_TIME) searchParams.push(['clear', 'true'])
				const response = await LightningApi.get('api/v2/alert/events', {
					searchParams: searchParams,
				})
					.then((res) => res.json())
					.catch((error) => {
						console.error('Error fetching historic lightning alerts:', error)
					})

				const arr = [...alerts, ...(response as IncomingLightningAlert[])]

				if ((response as IncomingLightningAlert[]).length >= PAGE_SIZE) return retrieveAlertPages(page + 1, arr)

				return arr
			}
			const response = await retrieveAlertPages()
			return response.map<LightningAlert>((alert) => ({
				protectionVrn: createTemporaryLightningProtectionVrn(alert.data.assetVrn, alert.data.priority),
				type: ProtectionType.Lightning,
				context: {},
				eventTime: alert.data.estClearTime,
				startTime: alert.data.lastTrigger,
				firstTrigger: alert.data.firstTrigger,
				currentState: alert.data.currentState,
				extent: alert.data.extent,
				firstEvent: alert.data.firstEvent,
				lastEvent: alert.data.lastEvent,
				priority: alert.data.priority,
				severity: lightningPriorityToSeverityMap[alert.data.priority],
				assetName: includeAssetName ? alert.data.assetName : undefined,
			}))
		},
		refetchOnMount: false, // Don't want to add staleTime as it might affect timeline queries but don't want to refetch every time the component mounts
		enabled,
	})
}

export const useHistoricalLightningAlerts = (startTime: Dayjs, endTime: Dayjs, enabled?: boolean, keepOldDataWhileFetching?: boolean) => {
	const impersonation = useImpersonation()
	return useQuery(
		historicalLightningAlertsQuery(startTime.toISOString(), endTime.toISOString(), impersonation, { enabled, keepOldDataWhileFetching }),
	)
}

const IncomingLightningAlertSchema = z.object({
	data: z.object({
		estClearTime: z.string(),
		firstTrigger: z.string(),
		lastTrigger: z.string(),
		assetVrn: z.string().startsWith('vrn'),
		priority: z.enum(IncomingLightningPriority),
		currentState: z.enum(['CLEAR', 'ACTIVE']),
		firstEvent: z.string(),
		lastEvent: z.string(),
		extent: z.string(),
		assetName: z.string().optional(),
	}),
})
type IncomingLightningAlert = z.infer<typeof IncomingLightningAlertSchema>

const IncomingLightningThreatzoneAlertSchema = z.object({
	horizon: z.number(),
	areaId: z.string(),
	eventTime: z.string(),
	assetVrn: z.string().startsWith('vrn'),
	priority: z.literal(ProtectionPriority.LightningAlarm),
})
type IncomingLightningThreatzoneAlert = z.infer<typeof IncomingLightningThreatzoneAlertSchema>
